/**
 * perfTelemetry — anonymous-ish per-session performance capture.
 *
 * Every editor session records the machine's GPU + a rolling FPS profile and
 * posts it to the backend (`POST /api/telemetry/perf`), where the admin
 * Performance panel on the dashboard aggregates it per user. The point: users
 * report "it runs at 30fps" with no way for us to see their GPU, resolution,
 * perf mode or where the time went — this gives debugging eyes on exactly the
 * low-end (GTX-1080-class and below) machines we need to tune for.
 *
 * Design constraints:
 *  - Zero per-frame cost beyond one comparison: the governor calls tick()
 *    every frame, but a sample is only taken once per second.
 *  - One cumulative doc per SESSION, re-posted (overwritten server-side) on
 *    each flush — storage stays bounded no matter how long the session runs.
 *  - The JWT rides on plain fetch via the global interceptor; unload flushes
 *    use fetch keepalive (sendBeacon can't carry the Authorization header).
 *  - Escape hatch: localStorage `synapse_telemetry_optout` = '1' disables all
 *    capture and posting.
 */

import { API_BASE } from '../api/apiBase';
import { BUILD_LABEL } from '../buildInfo';
import { PERF, RENDER_STATS } from './adaptiveBudget';
import { useEditorStore } from '../store/editorStore';
import { useFlowStore } from '../editors/flow/flowStore';

const FLUSH_MS = 45_000;
const FIRST_FLUSH_MS = 20_000;
/** Cap on stored per-second samples (~2h). Beyond it, decimate by 2. */
const MAX_SAMPLES = 7200;

interface DeviceInfo {
  gpu: string;
  gpu_vendor: string;
  ua: string;
  cores: number | null;
  memory_gb: number | null;
  screen: [number, number];
  device_pixel_ratio: number;
  /** WebGL context facts — webgl2 availability decides if GPU timing exists. */
  webgl2: boolean;
  antialias: boolean;
  max_texture_size: number;
  /** Which build this session ran (reviewer ask: tie a report to a version). */
  build: string;
}

/** One "worst second" snapshot — enough context to find WHERE it dropped. */
interface WorstSecond {
  fps: number;
  beat: number;
  calls: number;
  tris: number;
  notes: number;
  playing: boolean;
}

interface SessionAcc {
  sessionId: string;
  editor: string;
  startedAt: number;
  device: DeviceInfo;
  fpsSamples: number[];
  fpsMax: number;
  /** Seconds spent in each perf mode. */
  modeSeconds: Record<string, number>;
  playingSeconds: number;
  scaleSum: number;
  scaleMin: number;
  callsSum: number;
  callsMax: number;
  trisSum: number;
  trisMax: number;
  notesDrawnMax: number;
  dprLast: number;
  canvas: [number, number];
  sampleCount: number;
  samplesAtLastFlush: number;
  flushSeq: number;
  lastSampleAt: number;
  lastFlushAt: number;
  // ── WHY-is-it-slow signals ────────────────────────────────────────────
  /** Frame hitches (from raw rAF deltas): >100ms, >250ms, longest ever. */
  hitch100: number;
  hitch250: number;
  longestFrameMs: number;
  /** CPU cost of the render dispatch (wrapped gl.render), ms. */
  cpuRenderSumMs: number;
  cpuRenderMaxMs: number;
  cpuRenderCount: number;
  /** GPU frame times from EXT_disjoint_timer_query_webgl2, ms (sparse). */
  gpuSamplesMs: number[];
  /** JS heap (Chrome-only performance.memory), MB. */
  heapUsedMaxMb: number;
  heapLimitMb: number;
  /** Live GL object counts (gl.info.memory) — leak canary. */
  texturesMax: number;
  geometriesMax: number;
  /** Times the tab went hidden (rAF pauses punch holes in the profile). */
  hiddenCount: number;
  /** The 3 worst seconds seen, with context (sorted worst-first). */
  worstSeconds: WorstSecond[];
}

let _acc: SessionAcc | null = null;
let _installedUnloadHooks = false;

function optedOut(): boolean {
  try { return localStorage.getItem('synapse_telemetry_optout') === '1'; } catch { return false; }
}

/** True when WebGL is running WITHOUT a hardware GPU (SwiftShader, Microsoft
 *  Basic Render Driver, llvmpipe…). Telemetry surfaced a user at 26fps whose
 *  "GPU" was software rasterization — no editor tuning can fix that; the user
 *  has to re-enable hardware acceleration. Exposed so the UI can say so. */
export function isSoftwareRenderer(gpu: string): boolean {
  return /swiftshader|microsoft basic|llvmpipe|software(\s|-)?(rasterizer|render)|angle \(microsoft, microsoft/i.test(gpu);
}

let _softwareGL = false;
/** Whether the CURRENT session's WebGL is software-rendered (set at init). */
export function sessionIsSoftwareGL(): boolean { return _softwareGL; }

/** True for integrated graphics (Intel HD/UHD/Iris, AMD Vega/Radeon Graphics,
 *  Apple's own). Telemetry showed a user with a discrete 2070 Super whose
 *  Chrome was nonetheless rendering on the Intel iGPU — worth telling them,
 *  since the OS-level fix is one setting away. */
export function isIntegratedGpu(gpu: string): boolean {
  return /intel\(r\)?\s+(hd|uhd|iris|graphics)|intel\s+(hd|uhd|iris)|radeon\(tm\)?\s+graphics|vega\s+\d+\s+graphics|microsoft basic/i.test(gpu);
}

let _integratedGPU = false;
/** Whether this session runs on integrated graphics (set at init). */
export function sessionIsIntegratedGPU(): boolean { return _integratedGPU; }

function readGpu(gl: { getContext(): WebGLRenderingContext | WebGL2RenderingContext }): { gpu: string; vendor: string } {
  try {
    const ctx = gl.getContext();
    const dbg = ctx.getExtension('WEBGL_debug_renderer_info');
    const gpu = dbg ? String(ctx.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : String(ctx.getParameter(ctx.RENDERER));
    const vendor = dbg ? String(ctx.getParameter(dbg.UNMASKED_VENDOR_WEBGL)) : String(ctx.getParameter(ctx.VENDOR));
    return { gpu, vendor };
  } catch {
    return { gpu: 'unknown', vendor: 'unknown' };
  }
}

function readGlFacts(gl: TelemetryRenderer): { webgl2: boolean; antialias: boolean; max_texture_size: number } {
  try {
    const ctx = gl.getContext();
    const webgl2 = typeof WebGL2RenderingContext !== 'undefined' && ctx instanceof WebGL2RenderingContext;
    return {
      webgl2,
      antialias: !!ctx.getContextAttributes()?.antialias,
      max_texture_size: Number(ctx.getParameter(ctx.MAX_TEXTURE_SIZE)) || 0,
    };
  } catch {
    return { webgl2: false, antialias: false, max_texture_size: 0 };
  }
}

function newSession(gl: TelemetryRenderer): SessionAcc {
  const { gpu, vendor } = readGpu(gl);
  _softwareGL = isSoftwareRenderer(gpu);
  _integratedGPU = !_softwareGL && isIntegratedGpu(gpu);
  const nav = navigator as Navigator & { deviceMemory?: number };
  return {
    sessionId: (crypto.randomUUID?.() ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`),
    editor: useEditorStore.getState().activeView,
    startedAt: Date.now(),
    device: {
      gpu, gpu_vendor: vendor,
      ua: navigator.userAgent,
      cores: nav.hardwareConcurrency ?? null,
      memory_gb: nav.deviceMemory ?? null,
      screen: [window.screen?.width ?? 0, window.screen?.height ?? 0],
      device_pixel_ratio: window.devicePixelRatio || 1,
      build: BUILD_LABEL,
      ...readGlFacts(gl),
    },
    fpsSamples: [],
    fpsMax: 0,
    modeSeconds: {},
    playingSeconds: 0,
    scaleSum: 0, scaleMin: 1,
    callsSum: 0, callsMax: 0,
    trisSum: 0, trisMax: 0,
    notesDrawnMax: 0,
    dprLast: 1,
    canvas: [0, 0],
    sampleCount: 0,
    samplesAtLastFlush: 0,
    flushSeq: 0,
    lastSampleAt: 0,
    lastFlushAt: performance.now() - (FLUSH_MS - FIRST_FLUSH_MS),
    hitch100: 0, hitch250: 0, longestFrameMs: 0,
    cpuRenderSumMs: 0, cpuRenderMaxMs: 0, cpuRenderCount: 0,
    gpuSamplesMs: [],
    heapUsedMaxMb: 0, heapLimitMb: 0,
    texturesMax: 0, geometriesMax: 0,
    hiddenCount: 0,
    worstSeconds: [],
  };
}

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.round((p / 100) * (sorted.length - 1))));
  return sorted[idx];
}

function buildPayload(a: SessionAcc): object {
  const sorted = [...a.fpsSamples].sort((x, y) => x - y);
  const n = sorted.length || 1;
  const below = (t: number) => (sorted.filter(v => v < t).length / n) * 100;
  const gpuSorted = [...a.gpuSamplesMs].sort((x, y) => x - y);
  return {
    session_id: a.sessionId,
    editor: a.editor,
    started_at: a.startedAt,
    flush_seq: a.flushSeq,
    device: a.device,
    stats: {
      seconds: a.sampleCount,
      playing_seconds: Math.round(a.playingSeconds),
      fps: {
        avg: +(a.fpsSamples.reduce((s, v) => s + v, 0) / n).toFixed(1),
        median: percentile(sorted, 50),
        p5: percentile(sorted, 5),
        p1: percentile(sorted, 1),
        min: sorted[0] ?? 0,
        // Max doubles as a display-refresh hint (a 144Hz monitor shows here).
        max: a.fpsMax,
        below50_pct: +below(50).toFixed(1),
        below30_pct: +below(30).toFixed(1),
      },
      render: {
        scale_avg: +(a.scaleSum / Math.max(1, a.sampleCount)).toFixed(3),
        scale_min: a.scaleMin,
        dpr_last: a.dprLast,
        calls_avg: Math.round(a.callsSum / Math.max(1, a.sampleCount)),
        calls_max: a.callsMax,
        tris_avg: Math.round(a.trisSum / Math.max(1, a.sampleCount)),
        tris_max: a.trisMax,
        notes_drawn_max: a.notesDrawnMax,
        canvas: a.canvas,
      },
      // WHERE the time goes: CPU dispatch vs GPU frame time. On a fill-bound
      // (weak) GPU, gpu_med >> cpu_avg; a draw-call-bound scene shows the
      // opposite. gpu_* is null when the timer extension isn't available.
      timing: {
        cpu_render_avg_ms: a.cpuRenderCount ? +(a.cpuRenderSumMs / a.cpuRenderCount).toFixed(2) : null,
        cpu_render_max_ms: a.cpuRenderCount ? +a.cpuRenderMaxMs.toFixed(1) : null,
        gpu_med_ms: gpuSorted.length ? +percentile(gpuSorted, 50).toFixed(2) : null,
        gpu_p95_ms: gpuSorted.length ? +percentile(gpuSorted, 95).toFixed(2) : null,
        gpu_samples: gpuSorted.length,
        hitch_100ms: a.hitch100,
        hitch_250ms: a.hitch250,
        longest_frame_ms: Math.round(a.longestFrameMs),
      },
      memory: {
        js_heap_max_mb: a.heapUsedMaxMb ? Math.round(a.heapUsedMaxMb) : null,
        js_heap_limit_mb: a.heapLimitMb ? Math.round(a.heapLimitMb) : null,
        textures_max: a.texturesMax,
        geometries_max: a.geometriesMax,
      },
      hidden_count: a.hiddenCount,
      worst_seconds: a.worstSeconds,
      mode_seconds: a.modeSeconds,
      chart_notes: chartNoteCount(),
    },
  };
}

/** Note count of the active chart — best effort, editor-specific. */
function chartNoteCount(): number {
  try {
    const st = useEditorStore.getState();
    if (st.activeView === 'flow') {
      return useFlowStore.getState().chart.notes.length;
    }
    const diff = st.beatmap?.difficulties?.[st.activeDifficulty];
    return diff?.notes?.length ?? 0;
  } catch {
    return 0;
  }
}

function flush(final: boolean): void {
  const a = _acc;
  if (!a || a.sampleCount === 0) return;
  // Skip a scheduled flush when nothing new happened; a final flush always goes.
  if (!final && a.sampleCount - a.samplesAtLastFlush < 3) return;
  a.samplesAtLastFlush = a.sampleCount;
  a.flushSeq++;
  const body = JSON.stringify(buildPayload(a));
  try {
    void fetch(`${API_BASE}/api/telemetry/perf`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: final,       // survives pagehide; regular flushes don't need it
    }).catch(() => { /* offline / backend down — the next flush retries */ });
  } catch { /* ignore */ }
}

interface TelemetryRenderer {
  getContext(): WebGLRenderingContext | WebGL2RenderingContext;
  getPixelRatio(): number;
  domElement: HTMLCanvasElement;
  info: { render: { calls: number; triangles: number }; memory: { geometries: number; textures: number } };
  // three's render(scene, camera) — kept loose so the wrap stays signature-agnostic.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  render: (...args: any[]) => unknown;
}

/** Cap on stored GPU frame-time samples (sparse — one every ~20 frames). */
const MAX_GPU_SAMPLES = 400;

/**
 * Wrap gl.render once per renderer: measures the CPU cost of the dispatch on
 * every frame, and every ~20th frame brackets the render in a GPU timer query
 * (EXT_disjoint_timer_query_webgl2) so we can tell CPU-bound from GPU-bound
 * machines. One query in flight at a time; disjoint results are discarded.
 * All query work is try/caught — a driver quirk must never break rendering.
 */
function instrumentRenderer(gl: TelemetryRenderer): void {
  const g = gl as TelemetryRenderer & { __telemetryPatched?: boolean };
  if (g.__telemetryPatched) return;
  g.__telemetryPatched = true;

  const ctx = gl.getContext();
  const gl2 = (typeof WebGL2RenderingContext !== 'undefined' && ctx instanceof WebGL2RenderingContext)
    ? ctx : null;
  const ext = gl2?.getExtension('EXT_disjoint_timer_query_webgl2') ?? null;
  let pending: WebGLQuery | null = null;
  let frameCounter = 0;

  const native = gl.render.bind(gl);
  gl.render = (...args: unknown[]) => {
    const a = _acc;
    if (!a) return native(...args);

    // Collect the previous query's result (a frame or more later).
    if (gl2 && ext && pending) {
      try {
        if (gl2.getQueryParameter(pending, gl2.QUERY_RESULT_AVAILABLE)) {
          const disjoint = gl2.getParameter(ext.GPU_DISJOINT_EXT);
          if (!disjoint) {
            const ns = gl2.getQueryParameter(pending, gl2.QUERY_RESULT) as number;
            if (a.gpuSamplesMs.length < MAX_GPU_SAMPLES) a.gpuSamplesMs.push(ns / 1e6);
          }
          gl2.deleteQuery(pending);
          pending = null;
        }
      } catch { pending = null; }
    }

    // Start a new query on a sparse cadence.
    let began = false;
    frameCounter++;
    if (gl2 && ext && !pending && frameCounter % 20 === 0 && a.gpuSamplesMs.length < MAX_GPU_SAMPLES) {
      try {
        pending = gl2.createQuery();
        if (pending) { gl2.beginQuery(ext.TIME_ELAPSED_EXT, pending); began = true; }
      } catch { pending = null; }
    }

    const t0 = performance.now();
    const out = native(...args);
    const dt = performance.now() - t0;
    a.cpuRenderSumMs += dt;
    a.cpuRenderCount++;
    if (dt > a.cpuRenderMaxMs) a.cpuRenderMaxMs = dt;

    if (began && gl2 && ext) {
      try { gl2.endQuery(ext.TIME_ELAPSED_EXT); } catch { /* ignore */ }
    }
    return out;
  };
}

/**
 * Start (or adopt) the session for the current editor. Called by the governor
 * on mount; safe to call again (editor switches start a fresh session so the
 * stats stay attributable to one editor).
 */
export function initPerfTelemetry(gl: TelemetryRenderer): void {
  if (optedOut()) return;
  const editor = useEditorStore.getState().activeView;
  if (_acc && _acc.editor === editor) { instrumentRenderer(gl); return; } // same editor — keep accumulating
  if (_acc) flush(true);                            // editor switch — close the old one
  _acc = newSession(gl);
  instrumentRenderer(gl);

  if (!_installedUnloadHooks) {
    _installedUnloadHooks = true;
    window.addEventListener('pagehide', () => flush(true));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') { _acc && _acc.hiddenCount++; flush(true); }
    });
  }
}

/**
 * Raw per-frame hook — called by the governor with the UNfiltered rAF delta,
 * BEFORE its stall filter, so hitches land here (a 300ms hiccup is exactly
 * what we want counted, and exactly what the FPS smoothing ignores).
 */
export function perfTelemetryFrame(deltaSeconds: number): void {
  const a = _acc;
  if (!a || deltaSeconds <= 0) return;
  const ms = deltaSeconds * 1000;
  // Ignore multi-second gaps: that's the tab being hidden, not a hitch.
  if (ms > 4000) return;
  if (ms > a.longestFrameMs) a.longestFrameMs = ms;
  if (ms > 250) a.hitch250++;
  else if (ms > 100) a.hitch100++;
}

/**
 * Per-frame hook (governor). Internally samples at 1Hz; flushes on a timer.
 * `fps` is the governor's smoothed estimate — the same number users see.
 */
export function perfTelemetryTick(gl: TelemetryRenderer, fps: number): void {
  const a = _acc;
  if (!a) return;
  const now = performance.now();
  if (now - a.lastSampleAt < 1000) return;
  a.lastSampleAt = now;

  const st = useEditorStore.getState();
  a.fpsSamples.push(Math.round(fps));
  if (a.fpsSamples.length > MAX_SAMPLES) {
    // Decimate: keep every other sample so long sessions stay bounded while
    // the distribution shape survives.
    a.fpsSamples = a.fpsSamples.filter((_, i) => i % 2 === 0);
  }
  a.sampleCount++;
  a.fpsMax = Math.max(a.fpsMax, Math.round(fps));
  a.modeSeconds[st.perfMode] = (a.modeSeconds[st.perfMode] ?? 0) + 1;
  if (st.playback?.playing) a.playingSeconds += 1;
  a.scaleSum += PERF.renderScale;
  a.scaleMin = Math.min(a.scaleMin, PERF.renderScale);
  const calls = gl.info.render.calls;
  const tris = gl.info.render.triangles;
  a.callsSum += calls; a.callsMax = Math.max(a.callsMax, calls);
  a.trisSum += tris; a.trisMax = Math.max(a.trisMax, tris);
  a.notesDrawnMax = Math.max(a.notesDrawnMax, RENDER_STATS.notes);
  a.dprLast = gl.getPixelRatio();
  a.canvas = [gl.domElement.width, gl.domElement.height];
  a.texturesMax = Math.max(a.texturesMax, gl.info.memory.textures);
  a.geometriesMax = Math.max(a.geometriesMax, gl.info.memory.geometries);

  // JS heap — Chrome-only performance.memory; the closest a page can get to
  // "how much is Chrome eating" (the OS-level process number isn't exposed).
  const mem = (performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } }).memory;
  if (mem) {
    a.heapUsedMaxMb = Math.max(a.heapUsedMaxMb, mem.usedJSHeapSize / 1048576);
    a.heapLimitMb = mem.jsHeapSizeLimit / 1048576;
  }

  // Worst-3 seconds with context — points at WHERE it dropped, not just how low.
  const worstCut = a.worstSeconds.length < 3 ? Infinity : a.worstSeconds[a.worstSeconds.length - 1].fps;
  const fpsInt = Math.round(fps);
  if (fpsInt < worstCut && fpsInt < 55) {
    a.worstSeconds.push({
      fps: fpsInt,
      beat: Math.round(st.playback.currentBeat * 10) / 10,
      calls, tris,
      notes: RENDER_STATS.notes,
      playing: !!st.playback?.playing,
    });
    a.worstSeconds.sort((x, y) => x.fps - y.fps);
    if (a.worstSeconds.length > 3) a.worstSeconds.length = 3;
  }

  if (now - a.lastFlushAt >= FLUSH_MS) {
    a.lastFlushAt = now;
    flush(false);
  }
}
